import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { getEmployeeGroup } from '../utils/employeeGroup';

export interface EmployeeTask {
  id: string;
  description: string;
  projectName: string;
  type: string;
  duration: string;
}

export interface MonitoredEmployee {
  employeeId: string;
  name: string;
  status: 'working' | 'not_tracking' | 'error' | 'unknown';
  isClockedIn: boolean;
  currentTask: EmployeeTask | null;
  idleMinutes: number | null;
  todayTotal: string;
  lastUpdated: number;
}

export interface EmployeeMonitorSummary {
  working: number;
  notTracking: number;
}

export interface EmployeeMonitorConfig {
  checkIntervalMinutes: number;
  idleThresholdMinutes: number;
  reminderIntervalMinutes: number;
}

export interface EmployeeMonitorStatus {
  isRunning: boolean;
  hasSession: boolean;
  lastCheckTime: number | null;
  employeeCount: number;
  summary: EmployeeMonitorSummary;
  employees: MonitoredEmployee[];
  config: EmployeeMonitorConfig;
}

export interface DashboardStats {
  timestamp: number;
  isMonitoring: boolean;
  lastCheck: number | null;
  totals: EmployeeMonitorSummary;
  longestIdleMinutes: number;
  projectBreakdown: Record<string, number>;
  idleAlerts: Array<{ name: string; idleMinutes: number }>;
}

export function useEmployeeMonitor(autoRefresh = true, refreshInterval = 30000) {
  const [status, setStatus] = useState<EmployeeMonitorStatus | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [elinkEmployeeNames, setElinkEmployeeNames] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialCheckRef = useRef(false);
  const isMountedRef = useRef(true); // Track if component is still mounted

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get<EmployeeMonitorStatus>(
        `${API_URL}/employee-monitor/status`
      );
      if (!isMountedRef.current) return null; // Don't update state if unmounted
      setStatus(response.data);
      setError(null);
      return response.data;
    } catch (err: any) {
      if (!isMountedRef.current) return null;
      setError(err.response?.data?.error || 'Failed to fetch status');
      return null;
    }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get<DashboardStats>(
        `${API_URL}/employee-monitor/dashboard`
      );
      if (!isMountedRef.current) return; // Don't update state if unmounted
      setDashboardStats(response.data);
      setError(null);
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err.response?.data?.error || 'Failed to fetch dashboard stats');
    }
  }, []);

  // Fetch eLink Design employees (from scrum tracking)
  const fetchElinkEmployees = useCallback(async () => {
    try {
      const response = await axios.get<Array<{ name: string }>>(`${API_URL}/employees`);
      if (!isMountedRef.current) return; // Don't update state if unmounted
      // Store names in lowercase for case-insensitive matching
      const names = new Set(response.data.map(e => e.name.trim().toLowerCase()));
      setElinkEmployeeNames(names);
    } catch (err: any) {
      // Non-critical error, don't set error state
    }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    await Promise.all([fetchStatus(), fetchDashboardStats()]);
    if (!isMountedRef.current) return;
    setLoading(false);
  }, [fetchStatus, fetchDashboardStats]);

  const startMonitor = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/employee-monitor/start`);
      if (!isMountedRef.current) return;
      await fetchStatus();
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err.response?.data?.error || 'Failed to start monitor');
    }
  }, [fetchStatus]);

  const stopMonitor = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/employee-monitor/stop`);
      if (!isMountedRef.current) return;
      await fetchStatus();
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err.response?.data?.error || 'Failed to stop monitor');
    }
  }, [fetchStatus]);

  const triggerCheck = useCallback(async () => {
    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      setError(null);
      // The check endpoint waits for completion before responding
      await axios.post(`${API_URL}/employee-monitor/check`);
      if (!isMountedRef.current) return;
      // Now fetch the updated data
      await fetchAll();
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err.response?.data?.error || 'Failed to trigger check');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [fetchAll]);

  // Initial load - fetch status first, then trigger check if no data
  useEffect(() => {
    // Reset mounted ref on mount
    isMountedRef.current = true;
    
    const initializeData = async () => {
      if (initialCheckRef.current) return; // Prevent double execution in StrictMode
      initialCheckRef.current = true;
      
      if (!isMountedRef.current) return;
      setLoading(true);
      
      // Fetch eLink Design employees list (for group filtering)
      await fetchElinkEmployees();
      if (!isMountedRef.current) return;
      
      // First, try to get existing status
      const existingStatus = await fetchStatus();
      if (!isMountedRef.current) return;
      await fetchDashboardStats();
      if (!isMountedRef.current) return;
      
      // If no employees have been checked yet, trigger an initial check
      if (!existingStatus || existingStatus.employeeCount === 0 || !existingStatus.lastCheckTime) {
        try {
          await axios.post(`${API_URL}/employee-monitor/check`);
          if (!isMountedRef.current) return;
          // Fetch the updated data
          await fetchStatus();
          if (!isMountedRef.current) return;
          await fetchDashboardStats();
        } catch (err: any) {
          if (!isMountedRef.current) return;
          setError(err.response?.data?.error || 'Failed to run initial check');
        }
      }
      
      if (!isMountedRef.current) return;
      setLoading(false);
      setInitialCheckDone(true);
    };
    
    initializeData();
    
    // Cleanup: mark as unmounted to prevent state updates
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Empty deps - only run once on mount

  // Auto-refresh (only after initial check is done)
  useEffect(() => {
    if (!initialCheckDone || !isMountedRef.current) return;
    
    if (autoRefresh && refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          fetchAll();
        }
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, refreshInterval, fetchAll, initialCheckDone]);

  // Helper function to determine employee group
  // Matches if the full name starts with any eLink employee first name
  // e.g., "Zachary McIntosh" matches "zachary", "Tim Smith" matches "tim"
  const resolveEmployeeGroup = useCallback(
    (fullName: string) => getEmployeeGroup(fullName, elinkEmployeeNames),
    [elinkEmployeeNames]
  );

  return {
    status,
    dashboardStats,
    loading,
    initialCheckDone,
    error,
    elinkEmployeeNames,
    getEmployeeGroup: resolveEmployeeGroup,
    refresh: fetchAll,
    startMonitor,
    stopMonitor,
    triggerCheck
  };
}

